<script>
import {
  isColumnVisible,
  shouldShowActions,
} from '@modules/consolidator/utils/column-visibility/column-visibility'
import { WALLET_ASSET_COLUMN_CONFIG } from '@modules/consolidator/constants/wallet-asset-columns'

export default {
  name: 'AssetsByGroupTableGroupRow',
  props: {
    readonly: { type: Boolean, default: false },
    group: { type: Object, required: true },
    showRentability: { type: Boolean, default: false },
    estatisticasClasse: { type: Object, default: () => ({}) },
    classRendText: { type: String, default: '' },
    mapClassRent: { type: Object, default: () => ({}) },
    benchmarksByClass: { type: Object, default: () => ({}) },
    classInicioFmtByName: { type: Object, default: () => ({}) },
    classVolFmtByName: { type: Object, default: () => ({}) },
    visibleColumns: { type: Array, default: () => [] },
    // Allowlist de acoes por CHAVE (`detalhes`, `editar`, ...). `null` = default,
    // o conjunto inteiro gateado por `readonly`. Array = exatamente essas.
    allowedActions: { type: Array, default: null },
    // Catalogo de colunas INJETAVEL. O default e o da carteira do cliente, entao
    // quem nao passa nada continua exatamente como antes; a carteira do grupo
    // passa o seu, que e o mesmo mais a coluna `membro`.
    columnsConfig: { type: Array, default: () => WALLET_ASSET_COLUMN_CONFIG },
  },
  computed: {
    columns() {
      return this.columnsConfig
    },
    name() {
      return this.group.name
    },
    stats() {
      return this.estatisticasClasse[this.name] || null
    },
    sumClass() {
      const meta = this.group.meta || {}
      return meta.sumNegative ? 'text-danger' : ''
    },
    rendimentoClass() {
      if (!this.stats) return ''
      if (this.stats.rendimento > 0) return 'text-success'
      if (this.stats.rendimento < 0) return 'text-danger'
      return ''
    },
    showAcoes() {
      return shouldShowActions({
        readonly: this.readonly,
        visibleColumns: this.visibleColumns,
        allowedActions: this.allowedActions,
      })
    },
    showBenchmarkMenu() {
      const currentUser = this.$currentUser()
      return Boolean(
        this.$isWhitelabel() && !currentUser.IS_CLIENTE && currentUser.IS_GESTOR
      )
    },
  },
  methods: {
    isVisible(column) {
      return isColumnVisible({ visibleColumns: this.visibleColumns, column })
    },
    handleSelectClasse(ev) {
      this.$emit('select-classe', this.group.selection.assets, ev)
    },
    handleOpenSaveBenchmark() {
      this.$emit('open-save-benchmark', this.name)
    },
  },
}
</script>

<template>
  <tr class="assets-by-class-table__table-classe">
    <template v-for="col in columns">
      <td
        v-if="col.cellType === 'text' && isVisible(col.key)"
        :key="'cell-' + col.key"
        class="assets-by-class-table__table-classe-column"
      ></td>

      <td
        v-else-if="col.key === 'ativo' && isVisible('ativo')"
        :key="'ativo-' + col.key"
        class="assets-by-class-table__table-classe-column"
      >
        <div class="assets-by-class-table__table-classe-column--checkbox">
          <v-simple-checkbox
            v-if="!readonly"
            class="p-0 m-0"
            dense
            hide-details
            :ripple="false"
            :value="group.selection.isChecked"
            :indeterminate="group.selection.isIndeterminate"
            color="primary"
            @input="handleSelectClasse"
          />
          <div>{{ name }}</div>
        </div>
      </td>

      <td
        v-else-if="col.key === 'instituicao' && isVisible('instituicao')"
        :key="'inst-' + col.key"
        class="assets-by-class-table__table-classe-column"
      ></td>

      <td
        v-else-if="col.key === 'saldo_valor' && isVisible('saldo_valor')"
        :key="'svalor-' + col.key"
        class="assets-by-class-table__table-classe-column assets-by-class-table__table-classe-column--money text-right"
      >
        <span class="assets-by-class-table__classe-sum" :class="sumClass">
          {{ group.meta.sumFmt }}
        </span>
      </td>

      <td
        v-else-if="col.key === 'saldo_pct' && isVisible('saldo_pct')"
        :key="'spct-' + col.key"
        class="assets-by-class-table__table-classe-column text-right"
      >
        <span class="assets-by-class-table__classe-pct" :class="sumClass">
          {{ group.meta.pctFmt }}
        </span>
      </td>

      <td
        v-else-if="col.key === 'saldo_data' && isVisible('saldo_data')"
        :key="'sdata-' + col.key"
        class="assets-by-class-table__table-classe-column"
      ></td>

      <template v-else-if="col.key === 'rent_valor'">
        <td
          v-if="showRentability && isVisible('rent_valor')"
          :key="'rvalor-' + col.key"
          class="assets-by-class-table__table-classe-column assets-by-class-table__table-classe-column--money text-right"
        >
          <div
            v-if="stats"
            :class="rendimentoClass"
            class="assets-by-class-table__classe-rend"
          >
            {{ classRendText }}
          </div>
        </td>

        <td
          v-if="showRentability && isVisible('rent_inicio')"
          :key="col.key + '-inicio'"
          class="assets-by-class-table__table-classe-column text-left"
        >
          <v-tooltip v-if="stats" right>
            <template v-slot:activator="{ on }">
              <strong class="mouse-hover" v-on="on">
                {{ mapClassRent[name] }}
              </strong>
            </template>
            <span>
              <template>
                <p
                  v-for="(entry, i) in benchmarksByClass[name] || []"
                  :key="entry.benchmark.id || i"
                  class="mb-0"
                >
                  {{ entry.text }}
                </p>
              </template>
              <p class="mb-0">
                Desde o início:
                <span class="assets-by-class-table__classe-inicio">
                  {{ classInicioFmtByName[name] }}
                </span>
              </p>
            </span>
          </v-tooltip>
        </td>

        <td
          v-if="showRentability && isVisible('rent_data')"
          :key="col.key + '-data'"
          class="assets-by-class-table__table-classe-column"
        ></td>
      </template>

      <td
        v-else-if="col.key === 'volatilidade' && isVisible('volatilidade')"
        :key="'vol-' + col.key"
        class="assets-by-class-table__table-classe-column text-right"
      >
        <div v-if="stats">
          <span class="assets-by-class-table__classe-vol">
            {{ classVolFmtByName[name] }}
          </span>
        </div>
      </td>

      <td
        v-else-if="col.key === 'recomendacao' && isVisible('recomendacao')"
        :key="'rec-' + col.key"
        class="assets-by-class-table__table-classe-column"
      ></td>

      <td
        v-else-if="col.key === 'comentario' && isVisible('comentario')"
        :key="'com-' + col.key"
        class="assets-by-class-table__table-classe-column"
      ></td>

      <td
        v-else-if="col.key === 'acoes' && showAcoes"
        :key="'acoes-' + col.key"
        class="assets-by-class-table__table-classe-column"
      >
        <div class="d-flex align-items-center justify-content-center">
          <div
            v-if="showBenchmarkMenu"
            class="assets-by-class-table__benchmark-menu"
          >
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  small
                  icon
                  :ripple="false"
                  v-bind="attrs"
                  v-on="on"
                >
                  <icon-svg name="mdi-dots-vertical" :size="16" color="#8E8E8E" />
                </v-btn>
              </template>
              <v-list dense>
                <v-list-item @click="handleOpenSaveBenchmark">
                  <v-list-item-title>Alterar benchmark padrão</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </td>
    </template>
  </tr>
</template>
